/**
 * Create Shift Modal Component
 *
 * Handles both single-day shifts and multi-day deployments in one unified
 * workflow. The End Date is optional:
 *   - Leave blank (or set equal to Start Date) → single-day shift.
 *     end_time < start_time is interpreted as overnight on the same date.
 *   - Set an End Date after the Start Date → multi-day deployment stored as
 *     ONE continuous record (flight escorts, offshore cover, residential
 *     camps). A soft warning banner appears when total duration > 72 hours;
 *     the scheduler must tick an acknowledgement to proceed.
 */
import React, { useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { TimeSelect } from './TimeSelect';
import { AlertTriangle, Clock } from 'lucide-react';
import { symbolFor } from '../../lib/currency';
import { useAuth } from '../../context/AuthContext';

const COMPLIANCE_COLORS = {
  compliant: 'text-[#1ABC9C]',
  expiring_soon: 'text-[#FFC04D]',
  expired: 'text-red-400',
  pending: 'text-[#A1A1AA]',
};

function hoursBetween(startDate, startTime, endDate, endTime) {
  if (!startDate || !startTime || !endTime) return null;
  // If end_date not provided, treat as same-day. Overnight is encoded as
  // end_time < start_time on the same date — bump the end date by one.
  let effectiveEndDate = endDate && endDate !== '' ? endDate : startDate;
  try {
    const s = new Date(`${startDate}T${startTime}:00`);
    let e = new Date(`${effectiveEndDate}T${endTime}:00`);
    if (effectiveEndDate === startDate && endTime <= startTime) {
      // overnight single-day case
      e = new Date(e.getTime() + 24 * 60 * 60 * 1000);
    }
    const diff = (e - s) / (1000 * 60 * 60);
    return diff > 0 ? diff : null;
  } catch {
    return null;
  }
}

export function CreateShiftModal({
  open,
  onOpenChange,
  newShift,
  setNewShift,
  onSave,
  jobTypes = [],
  contracts = [],
  medics = [],
  mode = 'create',
}) {
  const { user } = useAuth();
  const sym = symbolFor(user?.organisation_currency);
  const endDateProvided = !!newShift.end_date && newShift.end_date !== newShift.date;
  const invalidRange = newShift.end_date && newShift.date && newShift.end_date < newShift.date;
  const isMultiDay = endDateProvided && !invalidRange;

  const totalHours = useMemo(
    () => hoursBetween(newShift.date, newShift.start_time, newShift.end_date, newShift.end_time),
    [newShift.date, newShift.start_time, newShift.end_date, newShift.end_time],
  );

  const durationLabel = useMemo(() => {
    if (!totalHours) return null;
    const days = Math.floor(totalHours / 24);
    const rem = Math.round(totalHours - days * 24);
    if (days === 0) return `${totalHours.toFixed(1)} hrs`;
    return `${totalHours.toFixed(1)} hrs (${days}d ${rem}h)`;
  }, [totalHours]);

  const longDeployment = totalHours !== null && totalHours > 72;

  const canSave =
    !!newShift.contract_id &&
    !!newShift.job_type_id &&
    !!newShift.postcode &&
    !invalidRange &&
    (!longDeployment || !!newShift.long_duration_ack);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['Manrope']">
            {mode === 'edit'
              ? (isMultiDay ? 'Edit Multi-Day Shift' : 'Edit Shift')
              : (isMultiDay ? 'Create Multi-Day Shift' : 'Create New Shift')}
          </DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">
            Add a start and end date — leave End Date blank (or equal to Start Date) for a single-day shift. Set a later End Date to create a continuous multi-day assignment.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={newShift.date}
                onChange={(e) => setNewShift(prev => ({ ...prev, date: e.target.value, long_duration_ack: false }))}
                className="bg-[#1A1A1A] border-white/10"
                data-testid="shift-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>End Date <span className="text-[#A1A1AA] text-xs">(optional)</span></Label>
              <Input
                type="date"
                value={newShift.end_date || ''}
                min={newShift.date || undefined}
                onChange={(e) => setNewShift(prev => ({ ...prev, end_date: e.target.value, long_duration_ack: false }))}
                className="bg-[#1A1A1A] border-white/10"
                data-testid="shift-end-date-input"
              />
            </div>
          </div>

          {invalidRange && (
            <div className="p-2 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-3 h-3" />
              End date must be on or after the start date.
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Shift Type</Label>
              <Select
                value={newShift.shift_type}
                onValueChange={(value) => setNewShift(prev => ({ ...prev, shift_type: value }))}
              >
                <SelectTrigger className="bg-[#1A1A1A] border-white/10" data-testid="shift-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#2B2B2B] border-white/10">
                  <SelectItem value="regular">Regular</SelectItem>
                  <SelectItem value="night">Night</SelectItem>
                  <SelectItem value="standby">Standby</SelectItem>
                  <SelectItem value="emergency">Emergency</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Site / Venue Name</Label>
              <Input
                value={newShift.location}
                onChange={(e) => setNewShift(prev => ({ ...prev, location: e.target.value }))}
                placeholder="e.g. St Thomas Hospital"
                className="bg-[#1A1A1A] border-white/10"
                data-testid="shift-location-input"
              />
            </div>
          </div>

          {/* Dedicated address + postcode so Nominatim gets a clean query. */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={newShift.address || ''}
                onChange={(e) => setNewShift(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Street + town"
                className="bg-[#1A1A1A] border-white/10"
                data-testid="shift-address-input"
              />
            </div>
            <div className="space-y-2">
              <Label>
                Postcode <span className="text-red-400">*</span>
              </Label>
              <Input
                value={newShift.postcode || ''}
                onChange={(e) => setNewShift(prev => ({
                  ...prev, postcode: e.target.value.toUpperCase(),
                }))}
                placeholder="e.g. SE1 7EH"
                className="bg-[#1A1A1A] border-white/10"
                data-testid="shift-postcode-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Time</Label>
              <TimeSelect
                value={newShift.start_time}
                onChange={(v) => setNewShift(prev => ({ ...prev, start_time: v, long_duration_ack: false }))}
                testIdPrefix="shift-start-time"
              />
            </div>
            <div className="space-y-2">
              <Label>End Time</Label>
              <TimeSelect
                value={newShift.end_time}
                onChange={(v) => setNewShift(prev => ({ ...prev, end_time: v, long_duration_ack: false }))}
                testIdPrefix="shift-end-time"
              />
            </div>
          </div>

          {/* Duration readout — shows for every valid range so the user can
              see at a glance how long the shift will be. */}
          {durationLabel && !invalidRange && (
            <div
              className="p-2 bg-[#1A1A1A] border border-white/10 rounded flex items-center gap-2 text-sm text-[#F2F2F2]"
              data-testid="shift-duration-readout"
            >
              <Clock className="w-4 h-4 text-[#1ABC9C]" />
              <span>Total duration:</span>
              <span className="font-semibold">{durationLabel}</span>
              {isMultiDay && (
                <span className="ml-auto text-xs text-indigo-300 bg-indigo-400/10 border border-indigo-400/30 px-2 py-0.5 rounded">
                  Multi-day
                </span>
              )}
            </div>
          )}

          {/* Soft >72h warning. User must acknowledge to save. */}
          {longDeployment && !invalidRange && (
            <div
              className="p-3 bg-[#FFC04D]/10 border border-[#FFC04D]/40 rounded text-sm"
              data-testid="shift-long-warning"
            >
              <div className="flex items-start gap-2 text-[#FFC04D] font-medium">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div>This shift is longer than 72 hours.</div>
                  <div className="text-[#F2F2F2] font-normal mt-1">
                    Please confirm this duration is correct. Longer continuous assignments (flight escorts, offshore cover, residential camps) are supported — tick the box to proceed, or amend the dates/times.
                  </div>
                  <label className="flex items-center gap-2 mt-3 cursor-pointer text-[#F2F2F2]">
                    <input
                      type="checkbox"
                      checked={!!newShift.long_duration_ack}
                      onChange={(e) => setNewShift(prev => ({ ...prev, long_duration_ack: e.target.checked }))}
                      className="w-4 h-4 accent-[#1ABC9C]"
                      data-testid="shift-long-duration-ack"
                    />
                    <span className="text-sm">
                      I confirm this duration ({durationLabel}) is correct and intentional.
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>
                Contract <span className="text-red-400">*</span>
              </Label>
              <Select
                value={newShift.contract_id || ''}
                onValueChange={(value) => setNewShift(prev => ({
                  ...prev,
                  contract_id: value,
                  job_type_id: '',
                  rate_payable: 0,
                  rate_chargeable: 0,
                }))}
              >
                <SelectTrigger className="bg-[#1A1A1A] border-white/10" data-testid="shift-contract-select">
                  <SelectValue placeholder="Select contract" />
                </SelectTrigger>
                <SelectContent className="bg-[#2B2B2B] border-white/10">
                  {contracts.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>
                Job Type <span className="text-red-400">*</span>
              </Label>
              <Select
                value={newShift.job_type_id || ''}
                onValueChange={(value) => {
                  const jobType = jobTypes.find((j) => j.id === value);
                  setNewShift(prev => ({
                    ...prev,
                    job_type_id: value,
                    rate_payable: jobType?.default_rate_payable ?? prev.rate_payable,
                    rate_chargeable: jobType?.default_rate_chargeable ?? prev.rate_chargeable,
                  }));
                }}
                disabled={!newShift.contract_id}
              >
                <SelectTrigger className="bg-[#1A1A1A] border-white/10" data-testid="shift-job-type-select">
                  <SelectValue placeholder={newShift.contract_id ? 'Select job type' : 'Pick contract first'} />
                </SelectTrigger>
                <SelectContent className="bg-[#2B2B2B] border-white/10">
                  {jobTypes
                    .filter((jt) => jt.contract_id === newShift.contract_id)
                    .map((jt) => (
                      <SelectItem key={jt.id} value={jt.id}>{jt.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Assign Medic (Optional — leave unassigned to allow self-accept)</Label>
            <Select
              value={newShift.medic_id || 'none'}
              onValueChange={(value) => setNewShift(prev => ({ ...prev, medic_id: value === 'none' ? '' : value }))}
            >
              <SelectTrigger className="bg-[#1A1A1A] border-white/10" data-testid="shift-medic-select">
                <SelectValue placeholder="Select a medic" />
              </SelectTrigger>
              <SelectContent className="bg-[#2B2B2B] border-white/10">
                <SelectItem value="none">Unassigned</SelectItem>
                {medics.map(medic => {
                  const statusIcon = medic.compliance_status === 'expired' ? '⚠️' :
                                    medic.compliance_status === 'expiring_soon' ? '⏰' :
                                    medic.compliance_status === 'compliant' ? '✓' : '';
                  return (
                    <SelectItem key={medic.id} value={medic.id}>
                      <span className={COMPLIANCE_COLORS[medic.compliance_status]}>{statusIcon}</span> {medic.name}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Rate Payable ({sym})</Label>
              <Input
                type="number"
                value={newShift.rate_payable}
                onChange={(e) => setNewShift(prev => ({ ...prev, rate_payable: e.target.value }))}
                className="bg-[#1A1A1A] border-white/10"
                data-testid="shift-rate-payable-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Rate Chargeable ({sym})</Label>
              <Input
                type="number"
                value={newShift.rate_chargeable}
                onChange={(e) => setNewShift(prev => ({ ...prev, rate_chargeable: e.target.value }))}
                className="bg-[#1A1A1A] border-white/10"
                data-testid="shift-rate-chargeable-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notes (Optional)</Label>
            <Textarea
              value={newShift.notes}
              onChange={(e) => setNewShift(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Add any notes..."
              className="bg-[#1A1A1A] border-white/10 resize-none"
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-[#A1A1AA]"
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={!canSave}
            className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="save-shift-btn"
          >
            {mode === 'edit'
              ? 'Save Changes'
              : (isMultiDay ? 'Create Multi-Day Shift' : 'Create Shift')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CreateShiftModal;
